import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { addRecentlyCreatedItem } from '@/hooks/useRealtimeSync';
import type { D2DUnit, D2DNote, D2DTag, D2DCreateUnitData } from '@/types/d2d';

/**
 * ==================================================================================
 * DEDICATED UNIT MANAGEMENT HOOK
 * ==================================================================================
 * This hook is ISOLATED from other D2D functionality to prevent breaking changes.
 * 
 * KEY FEATURES:
 * - Optimistic UI updates for instant feedback
 * - Realtime subscriptions for cross-device sync
 * - Protected from changes to other D2D features
 * 
 * IMPORTANT: Do NOT modify this hook when changing other D2D features!
 * ==================================================================================
 */

interface UseUnitManagementProps {
  userId: string | undefined;
  isOnline?: boolean;
}

export const useUnitManagement = ({ userId, isOnline = true }: UseUnitManagementProps) => {
  const [units, setUnits] = useState<D2DUnit[]>([]);
  
  // Helper function to check if a unit is a hot lead
  // CRITICAL: KI and NO tags ALWAYS disable HOT status, regardless of other conditions
  const isHotLead = (unit: D2DUnit): boolean => {
    const tags = unit.tags || [];
    
    // KI and NO always override - unit is NOT hot if these tags are present
    if (tags.includes('KI') || tags.includes('NO')) return false;
    
    // WIN also disables HOT status
    if (tags.includes('WIN')) return false;
    
    // Check for explicit HOT tag
    if (tags.includes('HOT')) return true;
    
    // KT with recent contact is considered hot
    if (tags.includes('KT') && unit.last_contact_at) {
      const daysSince = (Date.now() - new Date(unit.last_contact_at).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince >= 3 && daysSince <= 7) return true;
    }
    
    return false;
  };

  // ==================================================================================
  // REALTIME SUBSCRIPTION FOR UNITS
  // ==================================================================================
  useEffect(() => {
    if (!userId) return;

    console.log('🔄 [UnitManagement] Setting up realtime subscription for d2d_units');

    const channel = supabase
      .channel('unit-management-units')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_units',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('🔥 [UnitManagement] Realtime d2d_units change:', payload.eventType, payload);
          
          if (payload.eventType === 'INSERT') {
            const newUnit = payload.new as D2DUnit;
            
            // Fetch notes for the new unit
            const { data: notes } = await supabase
              .from('d2d_notes')
              .select('*')
              .eq('unit_id', newUnit.id)
              .eq('user_id', userId)
              .order('created_at', { ascending: false });
            
            const unitWithNotes = {
              ...newUnit,
              notes: notes || [],
              notes_count: notes?.length || 0,
              is_hot: isHotLead(newUnit)
            };

            // CRITICAL FIX: More aggressive temp unit replacement
            setUnits(prev => {
              // First, check if this exact unit already exists (by real ID)
              const existingRealUnit = prev.find(u => u.id === newUnit.id && !u.id.startsWith('temp-'));
              if (existingRealUnit) {
                console.log('✅ [UnitManagement] Unit already exists, updating:', newUnit.id);
                return prev.map(u => u.id === newUnit.id ? unitWithNotes : u);
              }
              
              // Find temp units with matching house_id and unit_name (likely the same unit)
              const matchingTempIndex = prev.findIndex(u => 
                u.id.startsWith('temp-') && 
                u.house_id === newUnit.house_id && 
                u.unit_name === newUnit.unit_name
              );
              
              if (matchingTempIndex >= 0) {
                // Replace matching temp unit with real unit
                console.log('✅ [UnitManagement] Replacing temp unit with real unit:', newUnit.id);
                const updated = [...prev];
                updated[matchingTempIndex] = unitWithNotes;
                return updated;
              }
              
              // Remove any other temp units and add the new one
              const withoutOldTempUnits = prev.filter(u => !u.id.startsWith('temp-'));
              console.log('✅ [UnitManagement] Adding new unit from realtime:', newUnit.id);
              return [...withoutOldTempUnits, unitWithNotes];
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedUnit = payload.new as D2DUnit;
            
            // Fetch notes
            const { data: notes } = await supabase
              .from('d2d_notes')
              .select('*')
              .eq('unit_id', updatedUnit.id)
              .eq('user_id', userId)
              .order('created_at', { ascending: false });
            
            const unitWithNotes = {
              ...updatedUnit,
              notes: notes || [],
              notes_count: notes?.length || 0,
              is_hot: isHotLead(updatedUnit)
            };

            setUnits(prev => prev.map(unit =>
              unit.id === updatedUnit.id ? unitWithNotes : unit
            ));
            
            console.log('✅ [UnitManagement] Updated unit from realtime:', updatedUnit.id);
          } else if (payload.eventType === 'DELETE') {
            const deletedUnit = payload.old as D2DUnit;
            setUnits(prev => prev.filter(unit => unit.id !== deletedUnit.id));
            console.log('✅ [UnitManagement] Deleted unit from realtime:', deletedUnit.id);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 [UnitManagement] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ==================================================================================
  // REALTIME SUBSCRIPTION FOR NOTES
  // ==================================================================================
  useEffect(() => {
    if (!userId) return;

    console.log('🔄 [UnitManagement] Setting up realtime subscription for d2d_notes');

    const channel = supabase
      .channel('unit-management-notes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_notes',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔥 [UnitManagement] Realtime d2d_notes change:', payload.eventType, payload);
          
          if (payload.eventType === 'INSERT') {
            const newNote = payload.new as D2DNote;
            
            setUnits(prev => prev.map(unit => {
              if (unit.id === newNote.unit_id) {
                // WICHTIG: Skip if note already exists (from optimistic update or previous realtime event)
                const noteExists = unit.notes?.some(n => n.id === newNote.id || n.id.startsWith('temp-'));
                if (noteExists) {
                  console.log('✅ [UnitManagement] Note already exists, skipping realtime insert:', newNote.id);
                  return unit;
                }
                
                console.log('✅ [UnitManagement] Adding note from realtime:', newNote.id);
                return {
                  ...unit,
                  notes: [...(unit.notes || []), newNote],
                  notes_count: (unit.notes_count || 0) + 1
                };
              }
              return unit;
            }));
          } else if (payload.eventType === 'DELETE') {
            const deletedNote = payload.old as D2DNote;
            
            setUnits(prev => prev.map(unit => {
              if (unit.id === deletedNote.unit_id) {
                const updatedNotes = (unit.notes || []).filter(n => n.id !== deletedNote.id);
                console.log('✅ [UnitManagement] Removed note from realtime:', deletedNote.id);
                return {
                  ...unit,
                  notes: updatedNotes,
                  notes_count: updatedNotes.length
                };
              }
              return unit;
            }));
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 [UnitManagement] Cleaning up notes subscription');
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ==================================================================================
  // CREATE UNIT WITH OPTIMISTIC UPDATE
  // ==================================================================================
  const createUnit = async (houseId: string, unitData: D2DCreateUnitData, options?: { silent?: boolean }): Promise<D2DUnit | null> => {
    if (!userId) return null;
    const silent = options?.silent ?? false;

    // Create temporary ID for optimistic update (so we can remove ONLY this unit on failure)
    const tempId = `temp-${crypto.randomUUID()}`;

    try {
      // Get user's organization_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userId)
        .single();

      if (profileError) throw profileError;

      if (!profile?.organization_id) {
        toast({
          title: "Fehler",
          description: "Keine Organisation gefunden.",
          variant: "destructive",
        });
        return null;
      }

      const tempUnit: D2DUnit = {
        id: tempId,
        house_id: houseId,
        user_id: userId,
        organization_id: profile.organization_id,
        unit_name: unitData.unit_name || undefined,
        tags: unitData.tags || [],
        next_reminder: undefined,
        reminder_reason: undefined,
        last_contact_at: new Date().toISOString(),
        status: 'active',
        offline_sync_pending: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        notes: [],
        notes_count: 0,
        is_hot: false,
        contact_attempts_count: 0,
      };

      // OPTIMISTIC UPDATE: Add unit immediately to local state
      console.log('⚡ [UnitManagement] Optimistic: Adding unit to UI immediately');
      setUnits(prev => [...prev, tempUnit]);

      // --- REQUIRED STEP: Create the unit in DB ---
      const { data, error } = await supabase
        .from('d2d_units')
        .insert([
          {
            house_id: houseId,
            user_id: userId,
            organization_id: profile.organization_id,
            unit_name: unitData.unit_name || null,
            tags: unitData.tags || [],
          },
        ])
        .select()
        .single();

      if (error) throw error;

      console.log('✅ [UnitManagement] Unit created in database:', data.id);

      // Track this item to prevent duplicate UI updates from realtime
      addRecentlyCreatedItem(data.id);

      // --- OPTIONAL STEPS (must never make unit creation "fail") ---

      // Handle NA tag with smart scheduling if applicable
      if (unitData.tags?.includes('NA')) {
        supabase.functions
          .invoke('smart-followup-scheduling', {
            body: {
              unit_id: data.id,
              visit_result: 'NA',
            },
          })
          .then(({ data: smartData, error: smartError }) => {
            if (smartError) {
              console.error('❌ [UnitManagement] Error in smart follow-up scheduling:', smartError);
            } else {
              console.log('✅ [UnitManagement] Smart follow-up scheduled:', smartData);
            }
          })
          .catch(err => {
            console.error('❌ [UnitManagement] Error calling smart follow-up scheduling:', err);
          });
      }

      // Add note if provided (best-effort)
      if (unitData.note) {
        (async () => {
          try {
            const { error: noteError } = await supabase
              .from('d2d_notes')
              .insert({
                unit_id: data.id,
                user_id: userId,
                note: unitData.note,
              });

            if (noteError) throw noteError;
          } catch (e) {
            console.warn('⚠️ [UnitManagement] Error adding note (ignored):', e);
          }
        })();
      }

      // Update house unit count (best-effort; do not block or fail unit creation)
      // NOTE: This used to cause false "Wohneinheit konnte nicht erstellt werden" when the house update timed out.
      (async () => {
        try {
          const { data: currentHouse, error: houseReadError } = await supabase
            .from('d2d_houses')
            .select('unit_count')
            .eq('id', houseId)
            .single();

          if (houseReadError) throw houseReadError;
          if (!currentHouse) return;

          const { error: houseUpdateError } = await supabase
            .from('d2d_houses')
            .update({ unit_count: (currentHouse.unit_count || 0) + 1 })
            .eq('id', houseId);

          if (houseUpdateError) throw houseUpdateError;
        } catch (e) {
          console.warn('⚠️ [UnitManagement] House unit_count update failed (ignored):', e);
        }
      })();

      if (!silent) {
        toast({
          title: "Wohneinheit erstellt",
          description: "Neue Wohneinheit wurde erfolgreich angelegt.",
        });
      }

      return data;
    } catch (error) {
      console.error('[UnitManagement] Error creating unit:', error);

      // Remove ONLY this optimistic unit on failure
      setUnits(prev => prev.filter(u => u.id !== tempId));

      toast({
        title: "Fehler",
        description: "Wohneinheit konnte nicht erstellt werden.",
        variant: "destructive",
      });
      return null;
    }
  };

  // ==================================================================================
  // CREATE UNITS BATCH - ULTRA-FAST using bulk_insert_units RPC (bypasses RLS per-row)
  // ==================================================================================
  const createUnitsBatch = async (
    houseId: string, 
    unitsData: Array<{ name: string; notes?: string }>,
    onProgress?: (created: number, total: number) => void
  ): Promise<D2DUnit[]> => {
    if (!userId || unitsData.length === 0) return [];

    // Generate temp IDs for optimistic updates
    const tempIds = unitsData.map(() => `temp-${crypto.randomUUID()}`);

    try {
      // Get user's organization_id ONCE
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userId)
        .single();

      if (!profile?.organization_id) {
        toast({
          title: "Fehler",
          description: "Keine Organisation gefunden.",
          variant: "destructive",
        });
        return [];
      }

      console.log(`⚡ [UnitManagement] FAST bulk creating ${unitsData.length} units via RPC...`);

      // Step 1: Add ALL units optimistically with temp IDs
      const optimisticUnits: D2DUnit[] = unitsData.map((unit, index) => ({
        id: tempIds[index],
        house_id: houseId,
        user_id: userId,
        organization_id: profile.organization_id,
        unit_name: unit.name || null,
        tags: [],
        status: 'active',
        offline_sync_pending: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        contact_attempts_count: 0,
        notes: [],
        notes_count: 0,
        is_hot: false,
      }));

      // Add all optimistic units immediately
      setUnits(prev => [...prev, ...optimisticUnits]);
      onProgress?.(0, unitsData.length);

      // Step 2: Use bulk_insert_units RPC for FAST insertion (single access check, bulk insert)
      const unitsPayload = unitsData.map(unit => ({
        unit_name: unit.name || null,
        notes: unit.notes || null,
      }));

      const { data: rpcResult, error: rpcError } = await supabase.rpc('bulk_insert_units', {
        p_house_id: houseId,
        p_organization_id: profile.organization_id,
        p_units: unitsPayload,
      });

      if (rpcError) {
        console.error('❌ [UnitManagement] RPC bulk_insert_units failed:', rpcError);
        throw rpcError;
      }

      console.log('✅ [UnitManagement] RPC bulk insert complete:', rpcResult);

      // Parse RPC result (array of {id, unit_name})
      const createdUnitIds = (rpcResult as Array<{ id: string; unit_name: string }>) || [];
      
      // Track created items
      createdUnitIds.forEach(u => addRecentlyCreatedItem(u.id));

      // Format created units
      const allCreatedUnits: D2DUnit[] = createdUnitIds.map((u, index) => ({
        id: u.id,
        house_id: houseId,
        user_id: userId,
        organization_id: profile.organization_id,
        unit_name: u.unit_name || null,
        tags: [],
        status: 'active',
        offline_sync_pending: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        contact_attempts_count: 0,
        notes: unitsData[index]?.notes ? [{
          id: `note-${u.id}`,
          unit_id: u.id,
          user_id: userId,
          note: unitsData[index].notes!,
          created_at: new Date().toISOString(),
        }] : [],
        notes_count: unitsData[index]?.notes ? 1 : 0,
        is_hot: false,
      }));

      // Replace ALL temp IDs with real IDs at once
      setUnits(prev => {
        const withoutTemp = prev.filter(u => !tempIds.includes(u.id));
        return [...withoutTemp, ...allCreatedUnits];
      });

      onProgress?.(unitsData.length, unitsData.length);

      // NOTE: house.unit_count is now updated inside bulk_insert_units function - no extra query needed!

      console.log(`✅ [UnitManagement] FAST bulk creation complete: ${allCreatedUnits.length} units created`);

      return allCreatedUnits;
    } catch (error) {
      console.error('[UnitManagement] Error batch creating units:', error);
      // Remove all temp units on complete failure
      setUnits(prev => prev.filter(u => !tempIds.includes(u.id)));
      toast({
        title: "Fehler",
        description: "Wohneinheiten konnten nicht erstellt werden.",
        variant: "destructive",
      });
      return [];
    }
  };

  // ==================================================================================
  // ADD NOTE WITH OPTIMISTIC UPDATE
  // ==================================================================================
  const addNote = async (unitId: string, note: string): Promise<void> => {
    if (!userId) return;

    // Reject adding notes to temporary units that haven't been saved to DB yet
    if (unitId.startsWith('temp-')) {
      console.warn('⚠️ [UnitManagement] Cannot add note to temporary unit. Please wait for unit to be created.');
      toast({
        title: "Bitte warten",
        description: "Wohneinheit wird noch erstellt. Bitte versuchen Sie es gleich noch einmal.",
        variant: "destructive",
      });
      throw new Error('Cannot add note to temporary unit');
    }

    try {
      // Create temporary note for optimistic update
      const tempNote: D2DNote = {
        id: `temp-${crypto.randomUUID()}`,
        unit_id: unitId,
        user_id: userId,
        note,
        created_at: new Date().toISOString()
      };

      // OPTIMISTIC UPDATE: Add note immediately to local state
      console.log('⚡ [UnitManagement] Optimistic: Adding note to UI immediately');
      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { 
              ...unit, 
              notes: [...(unit.notes || []), tempNote],
              notes_count: (unit.notes_count || 0) + 1
            }
          : unit
      ));

      // Insert into database
      const { data, error } = await supabase
        .from('d2d_notes')
        .insert({
          unit_id: unitId,
          user_id: userId,
          note: note,
        })
        .select()
        .single();

      if (error) throw error;

      console.log('✅ [UnitManagement] Note added to database:', data.id);

      // KRITISCH: Replace temp note with real note IMMEDIATELY to prevent race conditions
      // This must happen BEFORE the realtime event arrives
      setUnits(prev => prev.map(unit => {
        if (unit.id === unitId) {
          // Remove ALL temp notes and add the real note
          const notesWithoutTemp = (unit.notes || []).filter(n => !n.id.startsWith('temp-'));
          return {
            ...unit,
            notes: [...notesWithoutTemp, data],
            notes_count: notesWithoutTemp.length + 1
          };
        }
        return unit;
      }));

      toast({
        title: "Notiz hinzugefügt",
        description: "Notiz wurde erfolgreich gespeichert.",
      });
    } catch (error) {
      console.error('[UnitManagement] Error adding note:', error);
      
      // Remove optimistic update on error
      setUnits(prev => prev.map(unit => {
        if (unit.id === unitId) {
          return {
            ...unit,
            notes: (unit.notes || []).filter(n => !n.id.startsWith('temp-')),
            notes_count: Math.max(0, (unit.notes_count || 0) - 1)
          };
        }
        return unit;
      }));
      
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  // ==================================================================================
  // DELETE NOTE WITH OPTIMISTIC UPDATE
  // ==================================================================================
  const deleteNote = async (noteId: string, unitId: string): Promise<void> => {
    if (!userId) return;

    try {
      // Store the note for potential rollback
      let deletedNote: D2DNote | null = null;
      
      // OPTIMISTIC UPDATE: Remove note immediately from local state
      console.log('⚡ [UnitManagement] Optimistic: Removing note from UI immediately');
      setUnits(prev => prev.map(unit => {
        if (unit.id === unitId) {
          deletedNote = (unit.notes || []).find(n => n.id === noteId) || null;
          const updatedNotes = (unit.notes || []).filter(n => n.id !== noteId);
          return {
            ...unit,
            notes: updatedNotes,
            notes_count: updatedNotes.length
          };
        }
        return unit;
      }));

      // Delete from database
      const { error } = await supabase
        .from('d2d_notes')
        .delete()
        .eq('id', noteId)
        .eq('user_id', userId);

      if (error) throw error;

      console.log('✅ [UnitManagement] Note deleted from database:', noteId);

      toast({
        title: "Notiz gelöscht",
        description: "Notiz wurde erfolgreich gelöscht.",
      });
    } catch (error) {
      console.error('[UnitManagement] Error deleting note:', error);
      
      // Rollback optimistic update on error
      // In practice, the realtime subscription will re-add the note if deletion failed
      
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  return {
    units,
    setUnits,
    createUnit,
    createUnitsBatch,
    addNote,
    deleteNote,
    isHotLead
  };
};
